import axios from 'axios';
import { WebStorageStateStore } from 'oidc-client-ts';
import { STORAGE_KEYS, WARNING_THRESHOLD_MS } from '../sessionConfig';
import { storeJSON, readJSON, removeStorageKey } from './storageUtils';
import { sessionLog } from './sessionLog';

// This stores the appConfig and the OIDC configuration for later use
export let appConfig = null;
export let oidcConfig = {};

// ============================================================================
// AUTH CONFIG STORAGE (for satellite apps)
// ============================================================================

/**
 * Store auth config in localStorage for satellite apps to read.
 * Called by main app (bbcedit) after fetching /api/config.
 *
 * Stores the config in the same structure that initOIDCAuthProviderConfig expects:
 * { provider, oidcAuth: { authority, clientId, domain, audience }, ... }
 *
 * @param {Object} config - The auth configuration from /api/config
 */
export const storeAuthConfig = (config) => {
  if (!config) {
    console.warn('[authConfigStorage] Cannot store null/undefined config');
    return;
  }

  // Store the full config structure that initOIDCAuthProviderConfig expects
  // The backend config has oidcAuth nested, so we preserve that structure
  const authConfig = {
    provider: config.provider,
    oidcAuth: config.oidcAuth,
    // Also store top-level fields that satellite apps might need
    authIdleTimeoutMinutes: config.authIdleTimeoutMinutes,
  };

  if (storeJSON(STORAGE_KEYS.AUTH_CONFIG, authConfig)) {
    sessionLog('[authConfigStorage] Auth config stored for satellite apps');
  }
};

/**
 * Get stored auth config from localStorage.
 * Called by satellite apps to get auth configuration.
 *
 * @returns {Object|null} The stored auth config, or null if not found
 */
export const getStoredAuthConfig = () => {
  const config = readJSON(STORAGE_KEYS.AUTH_CONFIG);
  if (!config) {
    sessionLog('[authConfigStorage] No stored auth config found');
  }
  return config;
};

/**
 * Clear stored auth config from localStorage.
 * Should be called on logout to clean up.
 */
export const clearStoredAuthConfig = () => {
  removeStorageKey(STORAGE_KEYS.AUTH_CONFIG);
  sessionLog('[authConfigStorage] Auth config cleared');
};

/**
 * Find the Auth0 localStorage key by prefix matching.
 * The SDK may sort scopes alphabetically, so we match on the stable prefix
 * `@@auth0spajs@@::{clientId}::{audience}::` instead of an exact scope string.
 *
 * @param {string} clientId - Auth0 client ID
 * @param {string} audience - Auth0 API audience
 * @returns {string|null} The matching localStorage key, or null
 */
export function findAuth0StorageKey(clientId, audience) {
  const prefix = `@@auth0spajs@@::${clientId}::${audience || 'default'}::`;
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(prefix)) {
      return key;
    }
  }
  return null;
}

/**
 * Check if a valid OIDC/Auth0 token exists in localStorage.
 * This checks based on the stored auth config without needing full initialization.
 *
 * @returns {boolean} True if a token exists (may still be expired)
 */
export const hasStoredToken = () => {
  try {
    const config = getStoredAuthConfig();
    if (!config) return false;

    const { provider, oidcAuth } = config;

    // GCP stores its ID token under a dedicated localStorage key
    if (provider === 'gcp') {
      return !!localStorage.getItem(STORAGE_KEYS.GCP_TOKEN);
    }

    if (!oidcAuth) return false;

    let storageKey;
    if (provider === 'auth0') {
      const { clientId, audience } = oidcAuth;
      storageKey = findAuth0StorageKey(clientId, audience);
    } else {
      const { authority, clientId } = oidcAuth;
      storageKey = `oidc.user:${authority}:${clientId}`;
    }

    if (!storageKey) {
      sessionLog('[authConfigStorage] No token storage key found');
      return false;
    }

    const tokenData = localStorage.getItem(storageKey);
    if (!tokenData) {
      sessionLog('[authConfigStorage] No token found with key:', storageKey);
      return false;
    }

    // Token exists - could still be expired, but that's handled by the auth provider
    sessionLog('[authConfigStorage] Token found');
    return true;
  } catch (e) {
    console.error('[authConfigStorage] Error checking for token:', e);
    return false;
  }
};

// ============================================================================
// OIDC AUTH PROVIDER CONFIG
// ============================================================================

/**
 * Get the OIDC authentication provider configuration.
 * This function constructs the configuration object required for OIDC authentication.
 * @param {Object} config - The application configuration object.
 * @param {Object} options - Additional options for the configuration.
 * @returns {Object} The OIDC authentication provider configuration.
 */
export function initOIDCAuthProviderConfig(config, { redirectPath = '/' } = {}) {
  appConfig = { ...config };
  const { oidcAuth, provider } = config;
  const { authority, clientId, domain, audience } = oidcAuth;

  let authConfig = {};
  const redirectUri = `${window.location.origin}${redirectPath}`;
  if (provider === 'auth0') {
    if (!domain) {
      throw new Error('Domain is required for Auth0 configuration');
    }
    authConfig = {
      domain,
      clientId,
      authorizationParams: {
        redirect_uri: redirectUri, // v2: snake_case, nested in authorizationParams
        audience, // v2: moved from top-level into authorizationParams
        scope: 'openid profile email offline_access', // v2: must be explicit — only 'openid' is auto-added
      },
      cacheLocation: 'localstorage',
      useRefreshTokens: true,
      useRefreshTokensFallback: true, // v2 default flipped to false — explicitly keep true for Safari ITP
      useFormData: false, // v2 default flipped to true — keep false to preserve JSON content-type for Auth0 Actions
    };
  } else {
    if (!authority || !clientId) {
      throw new Error('Authority and clientId are required for OIDC configuration');
    }

    sessionLog('silent_redirect_uri', `${window.location.origin}/silent-renew.html`);
    authConfig = {
      authority,
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'aws.cognito.signin.user.admin email openid profile',
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      automaticSilentRenew: false, // Disabled - useOIDCSession handles refresh with activity tracking
      silent_redirect_uri: `${window.location.origin}/silent-renew.html`, // Dedicated silent refresh page
      // Notify before expiry - aligns with WARNING_THRESHOLD_MS in sessionConfig.js
      // This triggers our activity check in useOIDCSession:
      // - If user is active: silent refresh proceeds
      // - If user is inactive: dialog is shown and silent renew is stopped
      accessTokenExpiringNotificationTime: WARNING_THRESHOLD_MS / 1000, // Convert ms to seconds
      onSigninCallback: (user) => {
        sessionLog('User signed in:', user);

        // 1. Check redirect target in OIDC state (most reliable — survives
        //    the authorization redirect via oidc-client-ts state management)
        const stateRedirect = user?.state?.redirectTarget;

        // 2. Fallback: check sessionStorage (legacy / backwards-compatible)
        const storedRedirect = sessionStorage.getItem(STORAGE_KEYS.OIDC_REDIRECT);
        if (storedRedirect) sessionStorage.removeItem(STORAGE_KEYS.OIDC_REDIRECT);

        const target = stateRedirect || storedRedirect;

        if (target && target.startsWith('/') && target !== redirectPath) {
          // Store the redirect target for RootLayoutInner to pick up
          // after the user profile (and permissions) have been fetched.
          // We can't navigate directly here because the permission cache
          // is empty (cleared during logout) — a hard reload would hit
          // the route permission guard before permissions are re-populated.
          sessionLog(
            'OIDC signin callback: storing deep-link redirect for post-profile navigation:',
            target
          );
          sessionStorage.setItem(STORAGE_KEYS.POST_LOGIN_REDIRECT, target);
        }

        // Clean up callback URL (remove code/state params)
        window.history.replaceState(null, '', redirectPath || '/');
      },
      onAccessTokenExpiring: () => {
        sessionLog('OIDC access token expiring, attempting silent refresh...');
      },
      onAccessTokenExpired: () => {
        sessionLog('OIDC access token expired');
      },
      onSilentRenewError: (error) => {
        console.error('OIDC silent renew error:', error);
      },
    };
  }

  oidcConfig = authConfig; // Store the configuration for later use
  return authConfig;
}

/**
 * Retrieve the OIDC authentication data from local storage.
 * @returns {Object|null} The parsed OIDC authentication data or null if not found.
 */
export function getAuthFromLocalStorage() {
  if (!oidcConfig || !appConfig) {
    // If oidcConfig is not set, we cannot retrieve the auth data
    return null;
  }
  try {
    const { provider: authProvider } = appConfig;
    const { authority } = oidcConfig;
    if (authProvider === 'auth0') {
      // Auth0 uses a different storage key format — use prefix matching
      // because the SDK may sort scopes differently across versions
      // v2: audience moved into authorizationParams, clientId stays top-level
      const clientId = oidcConfig.clientId;
      const audience = oidcConfig.authorizationParams?.audience ?? oidcConfig.audience;
      const storageKey = findAuth0StorageKey(clientId, audience);
      if (!storageKey) {
        console.warn('No Auth0 auth data found in local storage (no matching key)');
        return null;
      }
      const authData = localStorage.getItem(storageKey);
      if (!authData) {
        console.warn('No Auth0 auth data found in local storage with key:', storageKey);
        return null;
      }
      const auth0StorageParsed = JSON.parse(authData).body;
      return auth0StorageParsed;
    }
    const { client_id: clientId } = oidcConfig;
    const storageKey = `oidc.user:${authority}:${clientId}`;
    const authData = localStorage.getItem(storageKey);
    if (!authData) {
      console.warn('No OIDC auth data found in local storage with key:', storageKey);
      return null;
    }
    const parsedData = JSON.parse(authData);
    return parsedData;
  } catch (error) {
    console.error('Error retrieving OIDC auth data from local storage:', error);
    return null;
  }
}

/**
 * Store a post-login redirect URL for deferred navigation.
 * Used by Auth0 and OIDC callbacks to defer deep-link redirects until
 * after the user profile (and permissions) have been fetched.
 *
 * @param {string} target - The redirect URL to store
 */
export function storePostLoginRedirect(target) {
  // Same-origin paths only. '//host' is a protocol-relative URL and '/\' is treated
  // like '//' by browsers — either would let a stored value navigate off-origin when
  // RootLayout later assigns it to window.location.href (open redirect).
  if (target && target.startsWith('/') && !target.startsWith('//') && !target.startsWith('/\\')) {
    sessionStorage.setItem(STORAGE_KEYS.POST_LOGIN_REDIRECT, target);
  }
}

/**
 * Consume (read + remove) the pending post-login redirect URL.
 * Called by RootLayoutInner after permissions are cached to navigate
 * to the deep-link the user originally requested before being sent to login.
 *
 * @returns {string|null} The redirect target, or null if none pending
 */
export function consumePostLoginRedirect() {
  const target = sessionStorage.getItem(STORAGE_KEYS.POST_LOGIN_REDIRECT);
  if (target) {
    sessionStorage.removeItem(STORAGE_KEYS.POST_LOGIN_REDIRECT);
  }
  return target;
}

/**
 * Fetch the configuration for the application.
 * @returns {Promise<Object>} The authorized configuration data.
 */
export async function fetchConfig(configEndpoint) {
  // Fetch the configuration and set up the application
  const { data: config } = await axios.get(configEndpoint);
  return config;
}

export function signOutOIDC(endSessionEndpoint, loginRoute) {
  const redirectPath = loginRoute || '/bbweb/login';
  if (!oidcConfig) {
    console.error('OIDC config not available for logout');
    return;
  }
  const { client_id: clientId } = oidcConfig;
  const redirectUri = `${window.location.origin}${redirectPath}`;

  // Use correct Cognito logout parameter
  const logoutUrl = `${endSessionEndpoint}?client_id=${clientId}&logout_uri=${encodeURIComponent(redirectUri)}`;
  window.location.href = logoutUrl;
}

export function polyfillAuth0(auth0) {
  if (!auth0) {
    return auth0;
  }

  // Polyfill the auth0 object to match the OIDC context
  auth0.user = { ...auth0.user, profile: auth0.user || {} };

  return auth0;
}
